/* eslint-disable @typescript-eslint/no-unused-vars */
import type { Maybe } from '@backmarket/utils/types/Maybe'
import { z } from 'zod/mini'

import { createBlockSchema } from '../schemas/create-block-schema'
import { type CouponApiProps, CouponApiPropsSchema } from '../shared/coupon'
import type { Equal, ExpectTrue } from '../types/validation'

import { ArticleCardsListSchema } from './article-cards-list'
import { type ApiBackground, ApiBackgroundSchema } from './background'
import type { _ApiBlock } from './block'
import { type CmsIcon, CmsIconSchema } from './icon'
import { type ProductQuery, ProductQuerySchema } from './product-query'
import { RefinedProductsApiPropsSchema } from './refined-products'

export type PersonalizedFeedItem = {
  technicalName?: Maybe<string>
  recommendation: _ApiBlock<'RefinedProducts'> | _ApiBlock<'ArticleCardsList'>
  saleEndDate?: Maybe<string>
  icon?: Maybe<CmsIcon>
  priority?: Maybe<number>
}

/* TypeScript */
export type PersonalizedFeedApiProps = {
  technicalName?: Maybe<string>
  title?: Maybe<string>
  feed: string
  fallbackV2: Array<PersonalizedFeedItem>
  additionalRecommendations: Array<PersonalizedFeedItem>
  forceSingleContent?: Maybe<boolean>
  background?: Maybe<ApiBackground>
  productQuery: {
    backbox: ProductQuery
    variant: ProductQuery
    parent?: Maybe<ProductQuery>
  }
  coupon?: Maybe<CouponApiProps>
  saleEndDate?: Maybe<string>
}

/* Schemas */
const PersonalizedFeedItemSchema = z.object({
  technicalName: z.nullish(z.string()),
  recommendation: z.union([
    createBlockSchema('RefinedProducts', RefinedProductsApiPropsSchema),
    createBlockSchema('ArticleCardsList', ArticleCardsListSchema),
  ]),
  saleEndDate: z.nullish(z.iso.datetime()),
  icon: z.nullish(CmsIconSchema),
  priority: z.nullish(z.number()),
})

export const PersonalizedFeedApiPropsSchema = z.object({
  technicalName: z.nullish(z.string()),
  title: z.nullish(z.string()),
  feed: z.string(),
  fallbackV2: z.array(PersonalizedFeedItemSchema),
  additionalRecommendations: z.array(PersonalizedFeedItemSchema),
  forceSingleContent: z.nullish(z.boolean()),
  background: z.nullish(ApiBackgroundSchema),
  productQuery: z.object({
    backbox: ProductQuerySchema,
    variant: ProductQuerySchema,
    parent: z.nullish(ProductQuerySchema),
  }),
  coupon: z.nullish(CouponApiPropsSchema),
  saleEndDate: z.nullish(z.iso.datetime()),
})

/* Schemas validation */
type TestPersonalizedFeed = ExpectTrue<
  Equal<
    PersonalizedFeedApiProps,
    z.infer<typeof PersonalizedFeedApiPropsSchema>
  >
>
